export const DEFAULT_FIT_OPTIONS = {
    l2: 0.01,
    learningRate: 0.05,
    iterations: 3000,
};
export function sigmoid(z) {
    if (z >= 0) {
        const ez = Math.exp(-z);
        return 1 / (1 + ez);
    }
    const ez = Math.exp(z);
    return ez / (1 + ez);
}
function standardizeFit(rows) {
    const n = rows.length;
    const dims = rows[0]?.length ?? 0;
    const means = new Array(dims).fill(0);
    for (const row of rows) {
        for (let j = 0; j < dims; j++)
            means[j] += row[j];
    }
    for (let j = 0; j < dims; j++)
        means[j] /= n;
    const stds = new Array(dims).fill(0);
    for (const row of rows) {
        for (let j = 0; j < dims; j++)
            stds[j] += (row[j] - means[j]) ** 2;
    }
    for (let j = 0; j < dims; j++) {
        stds[j] = Math.sqrt(stds[j] / n) || 1; // constant feature: avoid divide-by-zero
    }
    return { means, stds };
}
function standardizeApply(row, means, stds) {
    return row.map((v, j) => (v - means[j]) / stds[j]);
}
/**
 * Fits weights + bias on standardized features via Adam-optimized gradient
 * descent on the L2-regularized logistic loss. `y` must be 0/1 labels
 * aligned with `X`'s rows; `featureNames` is stored on the model purely for
 * traceability (e.g. confirming a caller's raw vector matches training order).
 */
export function fitLogisticRegression(featureNames, X, y, options = {}) {
    if (X.length !== y.length || X.length === 0) {
        throw new Error('fitLogisticRegression: X and y must be the same non-zero length.');
    }
    if (X.some((row) => row.length !== featureNames.length)) {
        throw new Error('fitLogisticRegression: every row must match featureNames.length.');
    }
    const opts = { ...DEFAULT_FIT_OPTIONS, ...options };
    const dims = featureNames.length;
    const { means, stds } = standardizeFit(X);
    const standardized = X.map((row) => standardizeApply(row, means, stds));
    const n = standardized.length;
    const weights = new Array(dims).fill(0);
    let bias = 0;
    const mW = new Array(dims).fill(0);
    const vW = new Array(dims).fill(0);
    let mB = 0;
    let vB = 0;
    const beta1 = 0.9;
    const beta2 = 0.999;
    const eps = 1e-8;
    for (let iter = 1; iter <= opts.iterations; iter++) {
        const gradW = new Array(dims).fill(0);
        let gradB = 0;
        for (let i = 0; i < n; i++) {
            const row = standardized[i];
            let z = bias;
            for (let j = 0; j < dims; j++)
                z += weights[j] * row[j];
            const err = sigmoid(z) - y[i];
            for (let j = 0; j < dims; j++)
                gradW[j] += err * row[j];
            gradB += err;
        }
        for (let j = 0; j < dims; j++) {
            gradW[j] = gradW[j] / n + opts.l2 * weights[j];
        }
        gradB /= n;
        for (let j = 0; j < dims; j++) {
            mW[j] = beta1 * mW[j] + (1 - beta1) * gradW[j];
            vW[j] = beta2 * vW[j] + (1 - beta2) * gradW[j] * gradW[j];
            const mHat = mW[j] / (1 - beta1 ** iter);
            const vHat = vW[j] / (1 - beta2 ** iter);
            weights[j] -= (opts.learningRate * mHat) / (Math.sqrt(vHat) + eps);
        }
        mB = beta1 * mB + (1 - beta1) * gradB;
        vB = beta2 * vB + (1 - beta2) * gradB * gradB;
        const mHatB = mB / (1 - beta1 ** iter);
        const vHatB = vB / (1 - beta2 ** iter);
        bias -= (opts.learningRate * mHatB) / (Math.sqrt(vHatB) + eps);
    }
    return { featureNames, weights, bias, featureMeans: means, featureStds: stds };
}
/** Predicts P(y=1) for one raw (unstandardized) feature vector. */
export function predictProbability(model, rawFeatures) {
    if (rawFeatures.length !== model.weights.length) {
        throw new Error('predictProbability: feature vector length mismatch with model.');
    }
    const standardized = standardizeApply(rawFeatures, model.featureMeans, model.featureStds);
    let z = model.bias;
    for (let j = 0; j < model.weights.length; j++)
        z += model.weights[j] * standardized[j];
    return sigmoid(z);
}
//# sourceMappingURL=logisticRegression.js.map